import React from "react";
import lotusIcon from "../assets/images/lotus-flower.png";
import {
  Box,
  Typography,
  Grid,
  Button,
  Alert,
  Card,
  CardContent,
  Avatar,
  Chip,
  Stack,
  Fade,
  Zoom,
} from "@mui/material";
import {
  Person,
  Email,
  Phone,
  CalendarToday,
  AccessTime,
  Category,
  Schedule,
  Euro,
  Notes,
  CheckCircle,
  EventAvailable,
  Security,
  Send,
  InfoOutlined,
  ThumbUp,
  Stars,
} from "@mui/icons-material";
import { dateUtils } from "../utils/dateUtils";

function BookingConfirmation({
  bookingData,
  selectedSlot,
  lessonType,
  onConfirm,
  loading,
}) {
  if (!selectedSlot || !lessonType || !bookingData) {
    return (
      <Alert severity="error">
        Errore: informazioni sulla prenotazione mancanti.
      </Alert>
    );
  }

  const bookingDetails = [
    {
      icon: <Person />,
      label: "Nome e Cognome",
      value: bookingData.userName,
    },
    {
      icon: <Email />,
      label: "Email",
      value: bookingData.userEmail,
    },
    {
      icon: <Phone />,
      label: "Telefono",
      value: bookingData.userPhone,
    },
  ];

  const lessonDetails = [
    {
      icon: <CalendarToday />,
      label: "Data",
      value: dateUtils.formatDate(selectedSlot.date, "EEEE, dd MMMM yyyy"),
    },
    {
      icon: <AccessTime />,
      label: "Orario",
      value: `${selectedSlot.startTime} - ${selectedSlot.endTime}`,
    },
    {
      icon: <Category />,
      label: "Tipo di Lezione",
      value: lessonType.name,
    },
    {
      icon: <Schedule />,
      label: "Durata",
      value: dateUtils.calculateDuration(selectedSlot.startTime, selectedSlot.endTime),
    },
  ];

  if (lessonType.price) {
    lessonDetails.push({
      icon: <Euro />,
      label: "Prezzo",
      value: `€${lessonType.price}`,
    });
  }

  return (
    <Box>
      {/* Hero Section */}
      <Card
        id="confirmation-header"
        elevation={4}
        sx={{
          mb: 4,
          background: '#145444',
          color: 'white',
          textAlign: 'center',
          scrollMarginTop: '100px'
        }}
      >
        <CardContent sx={{ py: 4 }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              mx: 'auto',
              mb: 2,
              backgroundColor: 'rgba(255,255,255,0.2)',
              fontSize: '2rem'
            }}
          >
            ✨
          </Avatar>
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            Ultimo Passo!
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Verifica i dettagli e conferma la tua prenotazione
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
            Sei a un click dal tuo percorso di crescita personale
          </Typography>
        </CardContent>
      </Card>

      {/* Premium Booking Summary */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Personal Information Card */}
        <Grid item xs={12} md={6}>
          <Fade in={true}>
            <Card elevation={3} sx={{ height: '100%' }}>
              <Box
                sx={{
                  background: '#145444',
                  color: 'white',
                  p: 2
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                    <Person />
                  </Avatar>
                  <Typography variant="h6" fontWeight="bold">
                    I tuoi Dati
                  </Typography>
                </Box>
              </Box>
              <CardContent sx={{ p: 3 }}>
                <Stack spacing={2}>
                  {bookingDetails.map((detail, index) => (
                    <Box key={index}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Avatar sx={{ width: 32, height: 32, backgroundColor: 'primary.light' }}>
                          {detail.icon}
                        </Avatar>
                        <Typography variant="subtitle2" color="primary.main" fontWeight="bold">
                          {detail.label}
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ ml: 5, fontWeight: '500' }}>
                        {detail.value}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Fade>
        </Grid>

        {/* Lesson Information Card */}
        <Grid item xs={12} md={6}>
          <Zoom in={true} style={{ transitionDelay: '200ms' }}>
            <Card elevation={3} sx={{ height: '100%' }}>
              <Box
                sx={{
                  background: '#145444',
                  color: 'white',
                  p: 2
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                    <EventAvailable />
                  </Avatar>
                  <Typography variant="h6" fontWeight="bold">
                    Dettagli Lezione
                  </Typography>
                </Box>
              </Box>
              <CardContent sx={{ p: 3 }}>
                <Stack spacing={2}>
                  {lessonDetails.map((detail, index) => (
                    <Box key={index}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Avatar sx={{ width: 32, height: 32, backgroundColor: 'success.light' }}>
                          {detail.icon}
                        </Avatar>
                        <Typography variant="subtitle2" color="success.dark" fontWeight="bold">
                          {detail.label}
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ ml: 5, fontWeight: '500' }}>
                        {detail.value}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>
      </Grid>

      {/* Notes and Description */}
      {(bookingData.notes || lessonType.description) && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {bookingData.notes && (
            <Grid item xs={12} md={lessonType.description ? 6 : 12}>
              <Card elevation={2}>
                <Box
                  sx={{
                    background: '#145444',
                    p: 2
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                      <Notes />
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold" color="white">
                      Le tue Note
                    </Typography>
                  </Box>
                </Box>
                <CardContent>
                  <Typography variant="body1" sx={{ fontStyle: 'italic', lineHeight: 1.6 }}>
                    "{bookingData.notes}"
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {lessonType.description && (
            <Grid item xs={12} md={bookingData.notes ? 6 : 12}>
              <Card elevation={2}>
                <Box
                  sx={{
                    background: '#145444',
                    p: 2
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                      <Stars />
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold" color="white">
                      Info Lezione
                    </Typography>
                  </Box>
                </Box>
                <CardContent>
                  <Typography variant="body1" color="textSecondary" sx={{ lineHeight: 1.6 }}>
                    {lessonType.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* What happens next */}
      <Card elevation={3} sx={{ mb: 4, overflow: 'hidden' }}>
        <Box
          sx={{
            background: '#145444',
            color: 'white',
            p: 3
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
              <InfoOutlined />
            </Avatar>
            <Typography variant="h5" fontWeight="bold">
              🚀 Cosa succede dopo?
            </Typography>
          </Box>
        </Box>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box textAlign="center">
                <Avatar sx={{ width: 60, height: 60, mx: 'auto', mb: 2, backgroundColor: 'success.light' }}>
                  <ThumbUp fontSize="large" />
                </Avatar>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="success.main">
                  1. Conferma Entro 24h
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Verifica e conferma definitiva della prenotazione
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box textAlign="center">
                <Avatar sx={{ width: 60, height: 60, mx: 'auto', mb: 2, backgroundColor: 'primary.light' }}>
                  <Send fontSize="large" />
                </Avatar>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="primary.main">
                  2. Dettagli via Email
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Tutti i dettagli della tua lezione nella tua casella
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box textAlign="center">
                <Avatar sx={{ width: 60, height: 60, mx: 'auto', mb: 2, backgroundColor: 'warning.light' }}>
                  <CalendarToday fontSize="large" />
                </Avatar>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="warning.main">
                  3. Promemoria
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Ti ricorderemo la lezione il giorno prima
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Terms and Conditions */}
      <Card elevation={2} sx={{ mb: 4 }}>
        <Box
          sx={{
            background: '#145444',
            p: 2
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
              <Security />
            </Avatar>
            <Typography variant="h6" fontWeight="bold" color="white">
              📋 Termini e Condizioni: cancellazioni gratuite fino a 12 ore prima della lezione seguendo le indicazioni qui riportate
            </Typography>
          </Box>
        </Box>
        <CardContent>
          <Stack spacing={2}>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <CheckCircle sx={{ color: 'success.main', mt: 0.5 }} />
              <Typography variant="body1">
                Confermando accetti le nostre <strong>condizioni di servizio</strong> e la policy sulla privacy
              </Typography>
            </Box>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <CheckCircle sx={{ color: 'success.main', mt: 0.5 }} />
              <Typography variant="body1">
                Se hai bisogno di cancellare la tua prenotazione puoi farlo gratuitamente fino a <strong>12 ore prima della lezione</strong> scrivendo a ilariastumpo.coaching@gmail.com
              </Typography>
            </Box>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <CheckCircle sx={{ color: 'success.main', mt: 0.5 }} />
              <Typography variant="body1">
                Per  <strong>emergenze dell’ultimo minuto</strong> contattami al numero 3463721890
              </Typography>
            </Box>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <CheckCircle sx={{ color: 'success.main', mt: 0.5 }} />
              <Typography variant="body1">
                I tuoi dati sono <strong>protetti e sicuri</strong> secondo il GDPR
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Premium Confirmation Button */}
      <Card
        elevation={6}
        sx={{
          background: '#145444',
          color: 'white',
          textAlign: 'center',
          mb: 2
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            <img
              src={lotusIcon}
              alt="Lotus"
              style={{ width: '28px', height: '28px', marginRight: '8px', verticalAlign: 'middle' }}
            />
            Sei pront* per il tuo viaggio interiore?
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, mb: 3 }}>
            Sei a un passo dal tuo percorso per riappropriarti del tuo Ben-essere
          </Typography>

          <Zoom in={true}>
            <Button
              variant="contained"
              size="large"
              onClick={onConfirm}
              disabled={loading}
              sx={{
                minWidth: { xs: "auto", sm: "280px" },
                width: { xs: "100%", sm: "auto" },
                py: 2,
                px: { xs: 3, sm: 4 },
                fontSize: "1.2rem",
                fontWeight: "bold",
                background: loading
                  ? 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)'
                  : 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                border: '3px solid rgba(255,255,255,0.3)',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: loading ? 'none' : 'translateY(-4px)',
                  boxShadow: loading ? 'none' : '0 12px 40px rgba(0,0,0,0.3)',
                  background: loading
                    ? 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)'
                    : 'linear-gradient(135deg, #45a049 0%, #4CAF50 100%)',
                },
                '&:disabled': {
                  color: 'white',
                  opacity: 0.8
                }
              }}
            >
              {loading ? "Invio in corso..." : "Conferma Prenotazione"}
            </Button>
          </Zoom>

          <Typography variant="caption" sx={{ display: 'block', mt: 2, opacity: 0.8 }}>
            🔒 Cliccando accetti automaticamente i nostri termini e condizioni
          </Typography>

          {/* Trust indicators */}
          <Box display="flex" justifyContent="center" alignItems="center" gap={2} mt={3}>
            <Chip
              icon={<Security />}
              label="Sicuro e Protetto"
              size="small"
              sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
            <Chip
              icon={<CheckCircle />}
              label="Cancellazione Gratuita"
              size="small"
              sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Loading State Enhancement */}
      {loading && (
        <Card elevation={3} sx={{ textAlign: 'center', py: 3, backgroundColor: 'primary.light' }}>
          <Typography variant="h6" color="primary.main" fontWeight="bold" gutterBottom>
            🎊 Creazione della tua prenotazione in corso...
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Ti invieremo una conferma a breve!
          </Typography>
        </Card>
      )}
    </Box>
  );
}

export default BookingConfirmation;